import React, { useState, useEffect, useRef } from "react";
import axios from "axios";

type TableData = {
  columns: string[];
  rows: Array<Array<string | number | boolean | null>>;
};

type DownloadLinks = Record<string, string>;

type ExportIntent = {
  format?: string;
};

type SqlMessage = {
  type: "user" | "assistant";
  text: string;
  table?: TableData | null;
  exportIntent?: ExportIntent | null;
  downloadLinks?: DownloadLinks | null;
};

type DownloadAction = {
  format: string;
  url: string;
};

type SqlChatWidgetProps = {
  newChatTrigger?: number;
  selectedConversationId?: string | number | null;
  selectedModel?: string;
  selectedProvider?: string;
};

const API_BASE_URL = `${(process.env.REACT_APP_API_URL || "").replace(/\/$/, "")}/api`;

const isMarkdownTableDivider = (line: string) => /^\|\s*:?-{3,}:?\s*(\|\s*:?-{3,}:?\s*)+\|?$/.test(line.trim());
const isMarkdownTableRow = (line: string) => /^\|.*\|$/.test(line.trim());

const parseMarkdownTableRow = (line: string): string[] =>
  line
    .trim()
    .replace(/^\|/, "")
    .replace(/\|$/, "")
    .split("|")
    .map((cell) => cell.trim());

const renderInlineMarkdown = (text: unknown): Array<string | JSX.Element> => {
  const parts = String(text).split(/(\*\*[^*]+\*\*)/g).filter(Boolean);

  return parts.map((part, index) => {
    const boldMatch = part.match(/^\*\*(.+)\*\*$/);
    if (boldMatch) {
      return (
        <strong key={index} className="font-semibold text-gray-900">
          {boldMatch[1]}
        </strong>
      );
    }
    return part;
  });
};

const renderInsightMarkdown = (text: unknown) => {
  const lines = String(text || "").split(/\r?\n/);
  const blocks: JSX.Element[] = [];
  let index = 0;

  while (index < lines.length) {
    const currentLine = lines[index].trimEnd();
    const trimmedLine = currentLine.trim();

    if (!trimmedLine) {
      index += 1;
      continue;
    }

    const headingMatch = trimmedLine.match(/^(#{1,6})\s+(.*)$/);
    if (headingMatch) {
      const level = headingMatch[1].length;
      const HeadingTag = (`h${level}` as keyof JSX.IntrinsicElements);
      const headingClasses: Record<number, string> = {
        1: "text-lg sm:text-xl font-bold text-emerald-950",
        2: "text-base sm:text-lg font-bold text-emerald-950",
        3: "text-sm sm:text-base font-semibold text-emerald-900",
        4: "text-sm font-semibold text-emerald-900",
        5: "text-sm font-semibold text-emerald-900",
        6: "text-sm font-semibold text-emerald-900",
      };

      blocks.push(
        <HeadingTag key={`heading-${index}`} className={headingClasses[level] || headingClasses[2]}>
          {renderInlineMarkdown(headingMatch[2])}
        </HeadingTag>,
      );
      index += 1;
      continue;
    }

    if (trimmedLine.startsWith("- ")) {
      const items: string[] = [];
      while (index < lines.length && lines[index].trim().startsWith("- ")) {
        items.push(lines[index].trim().slice(2));
        index += 1;
      }
      blocks.push(
        <ul key={`list-${index}`} className="list-disc space-y-1 pl-5 text-sm text-gray-700">
          {items.map((item, itemIndex) => (
            <li key={`list-item-${index}-${itemIndex}`}>{renderInlineMarkdown(item)}</li>
          ))}
        </ul>,
      );
      continue;
    }

    if (isMarkdownTableRow(trimmedLine) && index + 1 < lines.length && isMarkdownTableDivider(lines[index + 1])) {
      const headerCells = parseMarkdownTableRow(trimmedLine);
      const rows: string[][] = [];
      index += 2;

      while (index < lines.length && isMarkdownTableRow(lines[index].trim())) {
        rows.push(parseMarkdownTableRow(lines[index]));
        index += 1;
      }

      blocks.push(
        <div key={`table-${index}`} className="overflow-x-auto rounded-xl border border-emerald-100 bg-white">
          <table className="min-w-full text-xs sm:text-sm">
            <thead className="bg-emerald-50 text-emerald-900">
              <tr>
                {headerCells.map((cell, cellIndex) => (
                  <th key={`table-header-${index}-${cellIndex}`} className="px-3 py-2 text-left font-semibold border-b border-emerald-100">
                    {renderInlineMarkdown(cell)}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((row, rowIndex) => (
                <tr key={`table-row-${index}-${rowIndex}`} className="odd:bg-white even:bg-emerald-50/40">
                  {row.map((cell, cellIndex) => (
                    <td key={`table-cell-${index}-${rowIndex}-${cellIndex}`} className="px-3 py-2 border-b border-emerald-100 text-gray-700 align-top">
                      {renderInlineMarkdown(cell || "-")}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>,
      );
      continue;
    }

    const paragraphLines = [trimmedLine];
    index += 1;

    while (
      index < lines.length &&
      lines[index].trim() &&
      !lines[index].trim().startsWith("- ") &&
      !lines[index].trim().match(/^(#{1,6})\s+(.*)$/) &&
      !(isMarkdownTableRow(lines[index].trim()) && index + 1 < lines.length && isMarkdownTableDivider(lines[index + 1]))
    ) {
      paragraphLines.push(lines[index].trim());
      index += 1;
    }

    blocks.push(
      <p key={`paragraph-${index}`} className="text-sm leading-relaxed text-gray-700 whitespace-pre-wrap">
        {renderInlineMarkdown(paragraphLines.join("\n"))}
      </p>,
    );
  }

  return blocks;
};

const containsMarkdownTable = (text: unknown): boolean => {
  const lines = String(text || "").split(/\r?\n/);
  for (let index = 0; index < lines.length - 1; index += 1) {
    if (isMarkdownTableRow(lines[index].trim()) && isMarkdownTableDivider(lines[index + 1])) {
      return true;
    }
  }
  return false;
};

const getDownloadActions = (exportIntent: ExportIntent | null | undefined, downloadLinks: DownloadLinks | null | undefined): DownloadAction[] => {
  if (!downloadLinks || typeof downloadLinks !== "object") {
    return [];
  }

  const normalizedEntries = Object.entries(downloadLinks)
    .filter(([, url]) => typeof url === "string" && url.trim())
    .map(([format, url]) => ({
      format: String(format).toLowerCase(),
      url,
    }));

  if (normalizedEntries.length === 0) {
    return [];
  }

  const requestedFormat = String(exportIntent?.format || "").toLowerCase();
  if (requestedFormat === "auto") {
    return normalizedEntries;
  }
  return normalizedEntries.filter((entry) => entry.format === requestedFormat);
};

const SqlChatWidget = ({ newChatTrigger = 0, selectedConversationId = null }: SqlChatWidgetProps) => {
  const [messages, setMessages] = useState<SqlMessage[]>([]);
  const [inputMessage, setInputMessage] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [conversationId, setConversationId] = useState<string | number | null>(null);
  const [copiedIndex, setCopiedIndex] = useState<number | null>(null);
  const messagesEndRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    setMessages([]);
    setInputMessage("");
    setIsLoading(false);
    setConversationId(null);
  }, [newChatTrigger]);

  useEffect(() => {
    if (!selectedConversationId) {
      return;
    }

    const loadConversationMessages = async () => {
      setIsLoading(true);
      try {
        const response = await axios.get(
          `${API_BASE_URL}/nlp/conversations/${selectedConversationId}/messages`,
          {
            withCredentials: true,
            params: {
              skip: 0,
              limit: 100,
            },
          },
        );

        const turns = Array.isArray(response?.data?.messages) ? response.data.messages : [];
        const hydratedMessages: SqlMessage[] = [];

        turns.forEach((turn: any) => {
          const userText = turn?.user_message?.text;
          const botMessage = turn?.bot_message;
          const botText = typeof botMessage === "string" ? botMessage : botMessage?.text;
          const exportIntent = botMessage?.export_intent || turn?.export_intent || null;
          const downloadLinks = botMessage?.download_links || turn?.download_links || null;

          if (userText) {
            hydratedMessages.push({ type: "user", text: userText });
          }

          if (botText) {
            hydratedMessages.push({
              type: "assistant",
              text: botText,
              exportIntent,
              downloadLinks,
            });
          }
        });

        setMessages(hydratedMessages);
        setConversationId(selectedConversationId);
      } catch (error) {
        console.error("[SqlChatWidget] Error loading conversation messages:", error);
        setMessages([{ type: "assistant", text: "Unable to load selected chat history." }]);
      } finally {
        setIsLoading(false);
      }
    };

    loadConversationMessages();
  }, [selectedConversationId]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const selectedAccounts = (() => {
    try {
      const raw = localStorage.getItem("selectedAccountAlignedList");
      if (!raw) return [];
      const parsed = JSON.parse(raw);
      if (!Array.isArray(parsed)) return [];
      return parsed.filter((item) => typeof item === "string" && item.trim());
    } catch {
      return [];
    }
  })();

  const sendNlpRequest = async (payload: Record<string, unknown>, currentConversationId: string | number | null, maxAttempts = 3) => {
    const endpoint = selectedAccounts.length > 0
      ? currentConversationId
        ? `${API_BASE_URL}/nlp/conversations/${currentConversationId}/ask?account_aligned=${encodeURIComponent(selectedAccounts.join(","))}`
        : `${API_BASE_URL}/nlp/conversations/ask?account_aligned=${encodeURIComponent(selectedAccounts.join(","))}`
      : currentConversationId
        ? `${API_BASE_URL}/nlp/conversations/${currentConversationId}/ask`
        : `${API_BASE_URL}/nlp/conversations/ask`;

    let lastErr: unknown = null;
    for (let attempt = 1; attempt <= maxAttempts; attempt++) {
      try {
        const response = await axios.post(endpoint, null, {
          withCredentials: true,
          params: payload,
        });

        if (response && response.data && (response.data.insight || response.data.error)) {
          return response;
        }

        lastErr = new Error("Response missing insight and error fields");
      } catch (err) {
        lastErr = err;
      }

      await new Promise((resolve) => setTimeout(resolve, 400 * attempt));
    }

    throw lastErr;
  };

  const handleSendMessage = async (text?: string) => {
    const queryToCheck = text || inputMessage;
    if (!queryToCheck.trim()) return;

    const userMessage: SqlMessage = { type: "user", text: queryToCheck };
    setMessages((prev) => [...prev, userMessage]);
    setInputMessage("");
    setIsLoading(true);

    try {
      const storedModelSettings = (() => {
        try {
          const raw = localStorage.getItem("aiModelSettings");
          return raw ? JSON.parse(raw) : {};
        } catch {
          return {};
        }
      })();

      const payload = {
        question: queryToCheck,
        include_sql: true,
        include_raw_data: false,
        include_suggestions: false,
        ...(storedModelSettings.provider && storedModelSettings.model
          ? { provider: storedModelSettings.provider, model: storedModelSettings.model }
          : {}),
      };

      const response = await sendNlpRequest(payload, conversationId, 3);
      const data = response?.data;

      if (!data) {
        throw new Error("Empty response from conversations API");
      }

      if (data.error) {
        throw new Error(data.error);
      }

      if (data.conversation_id && !conversationId) {
        setConversationId(data.conversation_id);
      }

      const tableColumns = Array.isArray(data?.data?.columns) ? (data.data.columns as string[]) : [];
      const tableRows = Array.isArray(data?.data?.rows)
        ? (data.data.rows as Array<Array<string | number | boolean | null>>)
        : [];
      const hasTableData = tableColumns.length > 0 && tableRows.length > 0;

      const assistantMessage: SqlMessage = {
        type: "assistant",
        text: data.insight || "No insight returned.",
        table: hasTableData ? { columns: tableColumns, rows: tableRows } : null,
        exportIntent: data.export_intent || null,
        downloadLinks: data.download_links || null,
      };

      setMessages((prev) => [...prev, assistantMessage]);
    } catch (error) {
      console.error("Error sending message:", error);
      const errorMessage: SqlMessage = {
        type: "assistant",
        text: "Sorry, I couldn't process your query. Please try again.",
      };
      setMessages((prev) => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCopy = (text: string, index: number) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopiedIndex(index);
      setTimeout(() => setCopiedIndex(null), 2000);
    });
  };

  const handleDownload = async (url: string, format: string) => {
    if (!url) return;

    try {
      const response = await axios.get(url, {
        withCredentials: true,
        responseType: "blob",
      });

      const contentDisposition = response?.headers?.["content-disposition"] || "";
      const fileNameMatch = contentDisposition.match(/filename\*=UTF-8''([^;]+)|filename=\"?([^\";]+)\"?/i);
      const rawFileName = fileNameMatch?.[1] || fileNameMatch?.[2] || "";
      const decodedFileName = rawFileName ? decodeURIComponent(rawFileName) : "";
      const safeFormat = String(format || "file").toLowerCase();
      const fallbackFileName = `export.${safeFormat}`;
      const fileName = decodedFileName || fallbackFileName;

      const blobUrl = window.URL.createObjectURL(response.data);
      const link = document.createElement("a");
      link.href = blobUrl;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(blobUrl);
    } catch (error) {
      console.error("[SqlChatWidget] Download failed:", error);
    }
  };

  const handleKeyPress = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Enter" && !event.shiftKey) {
      event.preventDefault();
      handleSendMessage();
    }
  };

  return (
    <div className="flex flex-col flex-1 min-h-0 bg-gradient-to-b from-emerald-50/50 to-indigo-50/40">
      <div className="flex-1 overflow-y-auto px-3 py-3 space-y-3 min-h-0">
        {messages.length === 0 ? (
          <div className="text-center py-5 px-3">
            <div className="text-3xl mb-2">📊</div>
            <h3 className="font-semibold text-emerald-900 mb-1 text-sm">Ask your Data</h3>
            <p className="text-xs text-gray-500 mb-4">Query the PE Benchmarking database in natural language.</p>
          </div>
        ) : (
          <>
            {messages.map((message, index) => (
              <div key={index} className={`flex flex-col ${message.type === "user" ? "items-end" : "items-start"}`}>
                <div className={`max-w-[88%] p-2.5 rounded-2xl ${message.type === "user" ? "bg-gradient-to-r from-emerald-700 to-indigo-700 text-white rounded-br-sm shadow-[0_8px_20px_rgba(16,24,40,0.2)]" : "bg-white/95 border border-emerald-100 text-gray-800 rounded-bl-sm shadow-sm backdrop-blur"}`}>
                  <div className="space-y-3">
                    {message.type === "assistant" ? renderInsightMarkdown(message.text) : (
                      <div className="text-xs sm:text-sm leading-relaxed whitespace-pre-wrap">{message.text}</div>
                    )}
                  </div>

                  {message.type === "assistant" && message.table && !containsMarkdownTable(message.text) && Array.isArray(message.table?.columns) && Array.isArray(message.table?.rows) && (
                    <div className="mt-3 overflow-x-auto rounded-xl border border-emerald-100 bg-white">
                      <table className="min-w-full text-xs sm:text-sm">
                        <thead className="bg-emerald-50 text-emerald-900">
                          <tr>
                            {message.table.columns.map((column, colIdx) => (
                              <th key={`col-${colIdx}-${column}`} className="px-3 py-2 text-left font-semibold border-b border-emerald-100">{column}</th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {message.table.rows.map((row, rowIdx) => (
                            <tr key={`row-${rowIdx}`} className="odd:bg-white even:bg-emerald-50/40">
                              {row.map((cell, cellIdx) => (
                                <td key={`cell-${rowIdx}-${cellIdx}`} className="px-3 py-2 border-b border-emerald-100 text-gray-700">
                                  {cell === null || cell === undefined ? "-" : String(cell)}
                                </td>
                              ))}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}

                  {message.type === "assistant" && (
                    <div className="mt-2 flex justify-end gap-2 flex-wrap">
                      {getDownloadActions(message.exportIntent, message.downloadLinks as DownloadLinks).map((action) => (
                        <button
                          key={`${index}-download-${action.format}`}
                          type="button"
                          onClick={() => handleDownload(action.url, action.format)}
                          className="inline-flex items-center gap-1 rounded-lg px-2 py-1 text-xs text-emerald-700 bg-emerald-50 hover:bg-emerald-100 transition-colors border border-emerald-200"
                          title={`Download ${action.format.toUpperCase()}`}
                        >
                          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                            <polyline points="7 10 12 15 17 10" />
                            <line x1="12" y1="15" x2="12" y2="3" />
                          </svg>
                          {`Download ${action.format.toUpperCase()}`}
                        </button>
                      ))}

                      <button
                        type="button"
                        onClick={() => handleCopy(message.text, index)}
                        className="inline-flex items-center gap-1 rounded-lg px-2 py-1 text-xs text-gray-400 hover:text-emerald-700 hover:bg-emerald-50 transition-colors"
                        title="Copy message"
                      >
                        {copiedIndex === index ? (
                          <>
                            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                              <polyline points="20 6 9 17 4 12" />
                            </svg>
                            Copied!
                          </>
                        ) : (
                          <>
                            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
                              <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
                            </svg>
                            Copy
                          </>
                        )}
                      </button>
                    </div>
                  )}
                </div>
              </div>
            ))}

            {isLoading && (
              <div className="flex justify-start">
                <div className="bg-white/95 border border-emerald-100 rounded-2xl rounded-bl-sm p-3 shadow-sm backdrop-blur">
                  <div className="flex gap-1">
                    <div className="w-1.5 h-1.5 bg-emerald-500 rounded-full animate-bounce" style={{ animationDelay: "0ms" }}></div>
                    <div className="w-1.5 h-1.5 bg-emerald-500 rounded-full animate-bounce" style={{ animationDelay: "150ms" }}></div>
                    <div className="w-1.5 h-1.5 bg-emerald-500 rounded-full animate-bounce" style={{ animationDelay: "300ms" }}></div>
                  </div>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </>
        )}
      </div>

      <div className="p-2.5 bg-white/95 border-t border-emerald-100 shrink-0 backdrop-blur">
        <div className="flex gap-2 relative">
          <input
            type="text"
            className="flex-1 pl-3 pr-9 py-2 bg-emerald-50/50 border border-emerald-200 rounded-full text-xs focus:ring-2 focus:ring-emerald-500/30 focus:border-emerald-400 outline-none transition-colors"
            placeholder="Ask about your data..."
            value={inputMessage}
            onChange={(event) => setInputMessage(event.target.value)}
            onKeyPress={handleKeyPress}
            disabled={isLoading}
          />
          <button
            onClick={() => handleSendMessage()}
            disabled={isLoading || !inputMessage.trim()}
            className="absolute right-1 top-1 p-1.5 bg-gradient-to-r from-emerald-700 to-indigo-700 text-white rounded-full hover:from-emerald-800 hover:to-indigo-800 transition-all shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <line x1="22" y1="2" x2="11" y2="13"></line>
              <polygon points="22 2 15 22 11 13 2 9 22 2"></polygon>
            </svg>
          </button>
        </div>
      </div>
    </div>
  );
};

export default SqlChatWidget;